import {
  AfterViewInit,
  Component,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  inject,
} from '@angular/core';

/** Todo lo que puede recibir foco dentro del dialogo. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Contador de instancias: da un id unico al titulo para el aria-labelledby. */
let modalSequence = 0;

/**
 * Modal del tablero. La piel (cristal, animacion de entrada, cabecera y pie)
 * vive en las clases `.z-modal*` de styles.scss; aqui solo esta el
 * comportamiento: Escape, click en el fondo, foco atrapado y scroll bloqueado.
 */
@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  standalone: false,
})
export class ModalComponent implements AfterViewInit, OnDestroy {

  @Input() public title = '';
  @Input() public wide = false;
  @Output() public closed = new EventEmitter<void>();

  @ViewChild('dialog') private dialog?: ElementRef<HTMLElement>;

  /** Id del titulo al que apunta el aria-labelledby del dialogo. */
  public readonly titleId = `z-modal-title-${++modalSequence}`;

  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);

  /** Modales abiertos: el scroll se libera cuando se cierra el ultimo. */
  private static openCount = 0;

  /** Foco de la pagina antes de abrir; se devuelve al cerrar. */
  private previousFocus: HTMLElement | null = null;

  /** true si el arrastre empezo en el fondo (no dentro del dialogo). */
  private pressedOnBackdrop = false;

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR CICLO DE VIDA                           */
  /* -------------------------------------------------------------------------- */

  public ngAfterViewInit(): void {
    this.previousFocus = this.document.activeElement as HTMLElement | null;
    this.lockScroll();
    this.dialog?.nativeElement.focus();
  }

  public ngOnDestroy(): void {
    this.releaseScroll();
    this.previousFocus?.focus();
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR CIERRE                               */
  /* -------------------------------------------------------------------------- */

  public close(): void {
    this.closed.emit();
  }

  /** Marca si el gesto nacio en el fondo: seleccionar texto y soltar fuera del
   *  dialogo no debe cerrar el modal. */
  public onBackdropPress(event: MouseEvent): void {
    this.pressedOnBackdrop = event.target === event.currentTarget;
  }

  public onBackdropClick(event: MouseEvent): void {
    const onBackdrop = this.pressedOnBackdrop && event.target === event.currentTarget;
    this.pressedOnBackdrop = false;

    if (onBackdrop) {
      this.close();
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR TECLADO                              */
  /* -------------------------------------------------------------------------- */

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  /** Mantiene el tabulador dentro del dialogo mientras esta abierto. */
  private trapFocus(event: KeyboardEvent): void {
    const root = this.dialog?.nativeElement;

    if (!root) {
      return;
    }

    const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter((element) => element.offsetParent !== null);
    const active = this.document.activeElement as HTMLElement | null;

    if (!items.length) {
      event.preventDefault();
      root.focus();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];

    if (!active || !root.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && (active === first || active === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR SCROLL DEL BODY                          */
  /* -------------------------------------------------------------------------- */

  /** Bloquea el scroll del fondo y compensa el ancho de la barra para que la
   *  pagina no salte al abrir. */
  private lockScroll(): void {
    ModalComponent.openCount += 1;

    if (ModalComponent.openCount > 1) {
      return;
    }

    const body = this.document.body;
    const gap = window.innerWidth - this.document.documentElement.clientWidth;

    // `overflow-y` y no el atajo `overflow`: el atajo pisaria el
    // `overflow-x: clip` del body (§8 del contrato) por `hidden`, que si crea
    // contenedor de desplazamiento y deja sin efecto todos los `position:
    // sticky` de la pagina — empezando por la barra de filtros.
    this.renderer.setStyle(body, 'overflow-y', 'hidden');

    if (gap > 0) {
      this.renderer.setStyle(body, 'padding-right', `${gap}px`);
    }
  }

  private releaseScroll(): void {
    ModalComponent.openCount = Math.max(0, ModalComponent.openCount - 1);

    if (ModalComponent.openCount > 0) {
      return;
    }

    this.renderer.removeStyle(this.document.body, 'overflow-y');
    this.renderer.removeStyle(this.document.body, 'padding-right');
  }
}
